import React, { useState, useEffect } from 'react';
import { authApi } from '../../services/authApi';
import './WelcomePage.css';

interface WelcomePageProps {
  onLogin: (userId: string, isAdmin?: boolean) => void;
}

type TabType = 'existing' | 'new';

const WelcomePage: React.FC<WelcomePageProps> = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState<TabType>('existing');
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Password setup form state
  const [setupUsername, setSetupUsername] = useState('');
  const [setupPassword, setSetupPassword] = useState('');
  const [setupConfirmPassword, setSetupConfirmPassword] = useState('');
  const [setupError, setSetupError] = useState('');
  const [setupSuccess, setSetupSuccess] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Dev-only: automatically log in with demo credentials
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (typeof window === 'undefined') return;

    const alreadyLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (alreadyLoggedIn) return;

    // Allow switching between user/admin via URL, e.g. ?admin=1 or ?role=admin
    const params = new URLSearchParams(window.location.search);
    const isAdminDemo =
      params.get('admin') === '1' ||
      params.get('admin') === 'true' ||
      params.get('role') === 'admin';

    const demoUsername = isAdminDemo ? 'admin' : 'user';
    const demoPassword = isAdminDemo ? 'admin' : 'user';

    (async () => {
      try {
        const response = await authApi.login({ username: demoUsername, password: demoPassword });
        if (response.success && response.token) {
          const userRole = response.user?.role?.toLowerCase();
          const isAdmin = isAdminDemo || userRole === 'admin';

          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('currentUser', demoUsername);
          localStorage.setItem('authToken', response.token);
          localStorage.setItem('userData', JSON.stringify(response.user));
          localStorage.setItem('userRole', isAdmin ? 'admin' : 'user');

          onLogin(demoUsername, isAdmin);
        } else {
          console.warn('Dev auto-login failed:', response.message || 'Unknown error');
        }
      } catch (e) {
        console.error('Dev auto-login error:', e);
      }
    })();
  }, [onLogin]);

  // Ensure there is only one scroll container on the welcome screen.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!username || !password) {
      setError('Please enter both username and password');
      setIsLoading(false);
      return;
    }

    try {
      const response = await authApi.login({ username, password });
      if (response.success && response.token) {
        // Debug: Log the response to see what we're getting
        console.log('Login response:', response);
        console.log('User role from response:', response.user?.role);
        
        // Determine if user is admin based on role from MB backend
        // Check for both 'admin' and case-insensitive match
        const userRole = response.user?.role?.toLowerCase();
        const isAdmin = userRole === 'admin';
        
        console.log('Is admin?', isAdmin, 'Role:', userRole);
        
        // Store login state and token in localStorage
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem('currentUser', username);
        localStorage.setItem('authToken', response.token);
        localStorage.setItem('userData', JSON.stringify(response.user));
        localStorage.setItem('userRole', isAdmin ? 'admin' : 'user');
        
        console.log('Stored userRole in localStorage:', isAdmin ? 'admin' : 'user');
        console.log('========================================');
        console.log(isAdmin ? '✅ ADMIN USER LOGGED IN - Will show AdminConsole' : '👤 REGULAR USER LOGGED IN - Will show MainApp');
        console.log('========================================');

        onLogin(username, isAdmin);
      } else {
        setError(response.message || 'Invalid username or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setSetupError('');
    setSetupSuccess('');
    setIsSettingPassword(true);

    // Validation
    if (!setupUsername || !setupPassword || !setupConfirmPassword) {
      setSetupError('Please fill in all fields');
      setIsSettingPassword(false);
      return;
    }

    if (setupPassword !== setupConfirmPassword) {
      setSetupError('Passwords do not match');
      setIsSettingPassword(false);
      return;
    }

    if (setupPassword.length < 8) {
      setSetupError('Password must be at least 8 characters long');
      setIsSettingPassword(false);
      return;
    }

    // Validate username format (name_surname)
    if (!setupUsername.includes('_')) {
      setSetupError('Username must be in format: name_surname (e.g., john_doe)');
      setIsSettingPassword(false);
      return;
    }

    try {
      const response = await authApi.setPassword({
        username: setupUsername.trim(),
        password: setupPassword,
      });

      if (response.success) {
        setSetupSuccess('Password set successfully! You can now log in.');
        // Clear form
        setSetupUsername('');
        setSetupPassword('');
        setSetupConfirmPassword('');
        // Switch to login tab after 2 seconds
        setTimeout(() => {
          setActiveTab('existing');
          setSetupSuccess('');
        }, 2000);
      } else {
        setSetupError(response.message || 'Failed to set password. Please try again.');
      }
    } catch (error) {
      console.error('Set password error:', error);
      setSetupError('Network error. Please try again.');
    } finally {
      setIsSettingPassword(false);
    }
  };

  return (
    <div className="welcome-page">
      <div className="welcome-topbar">
        <img
          src="/RIOREGIE_REMONDIS.svg"
          alt="RioRegie : Remondis"
          className="welcome-topbar-logo"
        />
      </div>
      <div className="welcome-container">
        <div className="welcome-header">
          <h1>Welcome to RioRegie</h1>
          <p>Please enter your credentials to continue</p>
        </div>
        
        {/* Tabs */}
        <div className="welcome-tabs">
          <button
            className={`welcome-tab ${activeTab === 'existing' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('existing');
              setError('');
              setSetupError('');
              setSetupSuccess('');
            }}
          >
            Existing Employee
          </button>
          <button
            className={`welcome-tab ${activeTab === 'new' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('new');
              setError('');
              setSetupError('');
              setSetupSuccess('');
            }}
          >
            New Employee
          </button>
        </div>

        {/* Existing Employee Login Form */}
        {activeTab === 'existing' && (
          <div className="login-section">
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="form-input"
                  disabled={isLoading}
                />
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <button 
                type="submit" 
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          </div>
        )}

        {/* New Employee Password Setup Form */}
        {activeTab === 'new' && (
          <div className="login-section">
            <h2>Set Your Password</h2>
            <p className="setup-instructions">
              Enter the username provided by your admin (format: <strong>name_surname</strong>) and choose a password.
            </p>
            <form onSubmit={handleSetPassword} className="login-form">
              <div className="form-group">
                <label htmlFor="setup-username">Username</label>
                <input
                  type="text"
                  id="setup-username"
                  value={setupUsername}
                  onChange={(e) => setSetupUsername(e.target.value)}
                  placeholder="name_surname (e.g., john_doe)"
                  className="form-input"
                  disabled={isSettingPassword}
                />
                <small className="form-hint">Format: name_surname (e.g., john_doe)</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="setup-password">Password</label>
                <input
                  type="password"
                  id="setup-password"
                  value={setupPassword}
                  onChange={(e) => setSetupPassword(e.target.value)}
                  placeholder="Enter your password (min. 8 characters)"
                  className="form-input"
                  disabled={isSettingPassword}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="setup-confirm-password">Confirm Password</label>
                <input
                  type="password"
                  id="setup-confirm-password"
                  value={setupConfirmPassword}
                  onChange={(e) => setSetupConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="form-input"
                  disabled={isSettingPassword}
                />
              </div>
              
              {setupError && <div className="error-message">{setupError}</div>}
              {setupSuccess && <div className="success-message">{setupSuccess}</div>}
              
              <button 
                type="submit" 
                className="login-button"
                disabled={isSettingPassword}
              >
                {isSettingPassword ? 'Setting Password...' : 'Set Password'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomePage; 